import React, { useEffect, useState } from "react";
import { Alert, Modal } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { leasingRequest } from "../../../../redux/Actions/orderAction";
import { useDispatch } from "react-redux";
import { FlopppyButton, FlopppyButtonCancel } from "../../ButtonWithLoading";
import TextInputModal from "../../TextInputModal";
import { CheckBoxInput, SelectForm } from "../../Buttons";

const LEASING_TERM_OPTIONS = [
  {
    value: "10",
    label: "10 months.",
  },
  {
    value: "24",
    label: "24 months.",
  },
  {
    value: "0",
    label: "Standart conditions",
  },
];

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const normalizedValue = `${value}`.replace(",", ".");
  const parsedValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const roundToTwo = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const normalizeLeasingTerm = (term) => {
  if (term === null || term === undefined || term === "") {
    return undefined;
  }

  if (`${term}` === "standard") {
    return "0";
  }

  const parsedTerm = Number(term);
  return Number.isFinite(parsedTerm) ? `${parsedTerm}` : undefined;
};

const calculateLeasingDiscount = ({ orderSum, advance, term }) => {
  const termFactor = {
    10: 1.1,
    24: 1.24,
  }[Number(term)];

  if (!termFactor) {
    return "";
  }

  const amount = toNumber(orderSum);
  const advanceAmount = toNumber(advance);
  const calculatedDiscount =  (amount - advanceAmount) - ((amount - advanceAmount) / termFactor);

  return roundToTwo(calculatedDiscount);
};

function LeasingComponent({
  p_leasing_number,
  p_leasing_discount,
  p_leasing_advance,
  p_leasing_cod = false,
  p_leasing_term,
  p_order_total,
  paymentTitle,
  orderId,
  leasing_discount_used,
}) {
  const dispatch = useDispatch();
  const shouldShowArteaLeasingFields = `${paymentTitle || ""}`
    .toLowerCase()
    .includes("artea");
  const [variables, setVariables] = useState({
    leasing_number: p_leasing_number,
    leasing_discount: p_leasing_discount,
    leasing_advance: p_leasing_advance,
    leasing_cod: p_leasing_cod,
    leasing_term: normalizeLeasingTerm(p_leasing_term),
  });

  const {
    leasing_number,
    leasing_discount,
    leasing_advance,
    leasing_cod,
    leasing_term,
  } = variables;

  const [alertText, setAlertText] = useState({
    success: "success",
    text: "",
    show: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!leasing_term) {
      return;
    }

    const calculatedDiscount = calculateLeasingDiscount({
      orderSum: p_order_total,
      advance: leasing_advance,
      term: leasing_term,
    });

    setVariables((prevVariables) => {
      if (prevVariables.leasing_discount === calculatedDiscount) {
        return prevVariables;
      }

      return {
        ...prevVariables,
        leasing_discount: calculatedDiscount,
      };
    });
  }, [leasing_advance, leasing_term, p_order_total]);

  const submitLeasing = ({ payload = variables, validate = true }) => {
    const normalizedTerm = payload.leasing_term === null || payload.leasing_term === undefined || payload.leasing_term === ""
      ? null
      : Number(payload.leasing_term);

    const normalizedPayload = {
      ...payload,
      leasing_term: normalizedTerm,
    };

    const validationErrors = {};
    if (validate && (!normalizedPayload.leasing_number || `${normalizedPayload.leasing_number}`.trim() === "")) {
      validationErrors.leasing_number = "Leasing agreement number is required";
    }
    if (
      validate
      && shouldShowArteaLeasingFields
      && (normalizedPayload.leasing_term === null || normalizedPayload.leasing_term === undefined || normalizedPayload.leasing_term === "")
    ) {
      validationErrors.leasing_term = "Leasing term is required";
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    dispatch(leasingRequest(orderId, normalizedPayload)).then((res) => {
      if (res.success) {
        setLoading(false);
        setAlertText({
          success: "success",
          text: "Successfully updated.",
          show: true,
        });
      } else {
        setLoading(false);
        setAlertText({
          success: "error",
          text: "An error occurred while updating..",
          show: true,
        });
      }
    });
  };

  const onSave = () => {
    submitLeasing({ payload: variables, validate: true });
  };

  const onClearAndSave = () => {
    const clearedVariables = {
      ...variables,
      leasing_number: "",
      leasing_discount: "",
      leasing_advance: "",
      leasing_cod: false,
      leasing_term: undefined,
    };

    Modal.confirm({
      title: "Clear leasing data?",
      content: "All leasing fields will be removed and saved.",
      okText: "Clear and save",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: () => {
        setVariables(clearedVariables);
        submitLeasing({ payload: clearedVariables, validate: false });
      },
    });
  };

  const onChangeHandler = (e) => {
    setVariables({
      ...variables,
      [e.target.name]: e.target.value,
    });
  };

  const onTermChange = (value) => {
    setVariables({
      ...variables,
      leasing_term: value,
      leasing_discount: value ? calculateLeasingDiscount({
        orderSum: p_order_total,
        advance: leasing_advance,
        term: value,
      }) : "",
    });

    if (errors.leasing_term) {
      setErrors({
        ...errors,
        leasing_term: undefined,
      });
    }
  };

  return (
    <div className="col-12 py-4">
      {leasing_discount_used && (
        <Alert
          message={"Leasing discount used"}
          type={"warning"}
          showIcon
        />
      )}
      {alertText.show && (
        <Alert message={alertText.text} type={alertText.success} showIcon />
      )}
      <div className="row">
        <div className="col-12 col-md-4 mb-2">
          <TextInputModal
            required={true}
            text="Leasing agreement number"
            placeholder="Enter"
            name="leasing_number"
            value={leasing_number}
            onChange={onChangeHandler}
            warning={errors.leasing_number}
          />
        </div>
        <div className="col-12 col-md-4 mb-2 mb-md-0">
          <TextInputModal
            type="number"
            text="Advances"
            placeholder="Enter"
            name="leasing_advance"
            value={leasing_advance}
            onChange={onChangeHandler}
          />
        </div>
        {shouldShowArteaLeasingFields && (
          <>
            <div className="col-12 col-md-4 mb-2">
              <SelectForm
                placeholder="Leasing term"
                options={LEASING_TERM_OPTIONS}
                value={leasing_term}
                onChange={onTermChange}
                allowClear={true}
                required={true}
                error={errors.leasing_term}
              />
            </div>
            <div className="col-12 col-md-4 mb-2">
              <TextInputModal
                type="number"
                text="Leasing discount, EUR"
                placeholder="Calculated automatically"
                name="leasing_discount"
                value={leasing_discount}
                disabled={true}
              />
            </div>
          </>
        )}
        <div className="col-12 col-md-4 d-flex mb-2 align-items-end justify-content-md-between">
          <CheckBoxInput
            choised="leasing_cod"
            name="leasing_cod"
            text="Leasing COD"
            checked={leasing_cod}
            onChange={(e) =>
              onChangeHandler({
                target: { name: "leasing_cod", value: e.target.checked },
              })
            }
          />
          <div className="mt-2 mt-md-0 ml-md-auto">
            <FlopppyButton loading={loading} onClick={() => onSave()} />
            <FlopppyButtonCancel loading={loading} onClick={onClearAndSave} className="ml-1 bg-danger grow" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LeasingComponent;
